import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Upload, User, Eye, EyeOff, Mail, UserCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignUp = ({ onSignUp }) => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profilePic, setProfilePic] = useState('');
  const [useAvatar, setUseAvatar] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const generateAvatar = (name) => {
    const initials = name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    const colors = [
      ['#FFC857', '#e7722eff'],
      ['#e7722eff', '#FFC857'],
      ['#FFC857', '#F5E3D7']
    ];

    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createLinearGradient(0, 0, 200, 200);
    gradient.addColorStop(0, randomColor[0]);
    gradient.addColorStop(1, randomColor[1]);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 200, 200);

    ctx.fillStyle = '#47030F';
    ctx.font = 'bold 80px Space Grotesk, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials || 'U', 100, 100);

    return canvas.toDataURL('image/png');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePic(reader.result);
        setUseAvatar(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !username.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const avatar = useAvatar ? generateAvatar(username) : profilePic;
      const response = await axios.post(`${API}/auth/signup`, {
        email: email.trim(),
        username: username.trim(),
        password: password,
        profile_pic: avatar
      });

      const { access_token, user } = response.data;

      localStorage.setItem('chatAppToken', access_token);
      localStorage.setItem('chatAppUser', JSON.stringify(user));

      onSignUp(user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error signing up:', error);
      const message = error.response?.data?.detail || 'Failed to create account. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundColor: '#2e0510'
      }}
    >


      <Card
        className="w-full max-w-md shadow-2xl border-2 relative z-10"
        style={{
          backgroundColor: '#260e10',
          borderColor: 'rgba(255, 200, 87, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }}
      >
        <CardHeader className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-7 h-7 animate-pulse" style={{ color: '#FFC857' }} />
            <CardTitle className="text-4xl font-bold" style={{ color: '#FFC857', textShadow: '0 0 20px rgba(255, 200, 87, 0.5)' }}>Sign Up</CardTitle>
          </div>
          <CardDescription className="text-base" style={{ color: '#F5E3D7' }}>Create your account to start chatting</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col items-center space-y-3">
              <Avatar
                className="w-24 h-24 border-4 glow-effect"
                style={{ borderColor: '#FFC857' }}
              >
                <AvatarImage src={useAvatar ? generateAvatar(username || 'User') : profilePic} />
                <AvatarFallback style={{ background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)', color: '#47030F' }} className="text-2xl font-bold">
                  <User className="w-10 h-10" />
                </AvatarFallback>
              </Avatar>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    borderColor: '#FFC857',
                    backgroundColor: 'rgba(255, 200, 87, 0.1)',
                    color: '#FFC857'
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUseAvatar(true);
                    setProfilePic('');
                  }}
                  style={{
                    borderColor: '#FFC857',
                    backgroundColor: 'rgba(255, 200, 87, 0.1)',
                    color: '#FFC857'
                  }}
                >
                  Avatar
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-11 border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Username</label>
              <div className="relative">
                <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#FFC857', opacity: 0.7 }} />
                <Input
                  type="text"
                  placeholder="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-11 h-11 border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-11 h-11 border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                  style={{
                    backgroundColor: '#311214',
                    color: '#F5E3D7'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: '#FFC857', opacity: 0.7 }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>Confirm Password</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-11 border-0 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                style={{
                  backgroundColor: '#311214',
                  color: '#F5E3D7'
                }}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-base font-bold transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: '#e74e4a',
                color: '#47030F',
                boxShadow: '0 4px 20px rgba(231, 78, 74, 0.4)',
                borderRadius: '12px'
              }}
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>

            <div className="text-center text-sm" style={{ color: '#F5E3D7' }}>
              Already have an account?{' '}
              <Link to="/signin" className="font-semibold hover:underline" style={{ color: '#FFC857' }}>
                Sign In
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUp;